import * as React from 'react';
import { useState, useCallback } from 'react';
import { Input, Button, Tag, Space, Typography, Alert, Spin, Tooltip, Collapse } from 'antd';
import { RobotOutlined, CloseCircleOutlined, SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axiosApiInstance from '../../../api/axiosClient';
import { generate } from '@ant-design/colors';
import { getPaletteColor } from './utils';

const { Text } = Typography;

export interface AISearchResult {
    total: number;
    data: API.Creators.Creator[];
    reasoning: string;
    filters: {
        tag_filters: string[];
        platform: string;
        search: string;
        reasoning: string;
        youtube_followers_min: number;
        youtube_followers_max: number;
        instagram_followers_min: number;
        instagram_followers_max: number;
        tiktok_followers_min: number;
        tiktok_followers_max: number;
        engagement_rate_min?: number;
        engagement_rate_max?: number;
        instagram_engagement_rate_min?: number;
        instagram_engagement_rate_max?: number;
        tiktok_engagement_rate_min?: number;
        tiktok_engagement_rate_max?: number;
        language?: string;
        region?: string;
        audience_demographics_platform?: string;
        audience_gender?: string;
        audience_gender_min_pct?: number;
        audience_age?: string;
        audience_age_min_pct?: number;
        audience_country?: string;
        audience_country_min_pct?: number;
        audience_language?: string;
        audience_language_min_pct?: number;
        require_audience_demographics?: boolean;
        require_engagement_rate?: boolean;
        instagram_follower_growth_min_pct?: number;
        instagram_follower_growth_months?: number;
        tiktok_follower_growth_min_pct?: number;
        tiktok_follower_growth_months?: number;
        youtube_follower_growth_min_pct?: number;
        youtube_follower_growth_months?: number;
        instagram_engagement_growth_min_pct?: number;
        instagram_engagement_growth_months?: number;
        tiktok_engagement_growth_min_pct?: number;
        tiktok_engagement_growth_months?: number;
    };
}

interface Props {
    onResults: (result: AISearchResult | null) => void;
    tagOptions?: any[]; // the raw tag options array from creatorTagsAtomById(0) – used to resolve colours
}

const PLATFORM_COLOR: Record<string, string> = {
    YouTube: '#ff0000',
    Instagram: '#c13584',
    TikTok: '#010101',
};

const platformFollowerLabel = (filters: AISearchResult['filters']): string[] => {
    const parts: string[] = [];
    const fmt = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n);
    if (filters.youtube_followers_min || filters.youtube_followers_max)
        parts.push(`YT ${fmt(filters.youtube_followers_min)}–${fmt(filters.youtube_followers_max)}`);
    if (filters.instagram_followers_min || filters.instagram_followers_max)
        parts.push(`IG ${fmt(filters.instagram_followers_min)}–${fmt(filters.instagram_followers_max)}`);
    if (filters.tiktok_followers_min || filters.tiktok_followers_max)
        parts.push(`TT ${fmt(filters.tiktok_followers_min)}–${fmt(filters.tiktok_followers_max)}`);
    return parts;
};

const formatER = (rate: number): string => {
    const pct = rate > 1 ? rate : rate * 100;
    return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2)}%`;
};

const engagementRateLabels = (filters: AISearchResult['filters']): string[] => {
    const parts: string[] = [];
    const add = (label: string, min?: number, max?: number) => {
        if (!min && !max) return;
        if (min && max) parts.push(`${label} ER ${formatER(min)}–${formatER(max)}`);
        else if (min) parts.push(`${label} ER ≥ ${formatER(min)}`);
        else if (max) parts.push(`${label} ER ≤ ${formatER(max)}`);
    };
    add('Any', filters.engagement_rate_min, filters.engagement_rate_max);
    add('IG', filters.instagram_engagement_rate_min, filters.instagram_engagement_rate_max);
    add('TT', filters.tiktok_engagement_rate_min, filters.tiktok_engagement_rate_max);
    return parts;
};

const resolvedAudiencePlatformLabel = (filters: AISearchResult['filters']): string => {
    const explicit = filters.audience_demographics_platform?.trim();
    if (explicit) {
        return explicit.toUpperCase();
    }
    switch (filters.platform) {
        case 'Instagram':
            return 'INSTAGRAM';
        case 'TikTok':
            return 'TIKTOK';
        case 'YouTube':
            return 'YOUTUBE';
        default:
            break;
    }
    const hasAudienceDims =
        filters.audience_gender ||
        filters.audience_age ||
        filters.audience_country ||
        filters.audience_language;
    if (filters.require_audience_demographics && !hasAudienceDims) {
        return 'ANY PLATFORM';
    }
    if (hasAudienceDims) {
        return 'INSTAGRAM';
    }
    return 'AUDIENCE';
};

const audienceLabels = (filters: AISearchResult['filters']): string[] => {
    const parts: string[] = [];
    const platform = resolvedAudiencePlatformLabel(filters);
    const pct = (value?: number) => (value && value > 0 ? value : 50);

    if (filters.audience_gender) {
        parts.push(`${platform} audience ${filters.audience_gender} ≥${pct(filters.audience_gender_min_pct)}%`);
    }
    if (filters.audience_age) {
        parts.push(`${platform} audience age ${filters.audience_age} ≥${pct(filters.audience_age_min_pct)}%`);
    }
    if (filters.audience_country) {
        parts.push(`${platform} audience ${filters.audience_country} ≥${pct(filters.audience_country_min_pct)}%`);
    }
    if (filters.audience_language) {
        parts.push(`${platform} audience lang ${filters.audience_language} ≥${pct(filters.audience_language_min_pct)}%`);
    }
    if (filters.require_audience_demographics) {
        parts.push(
            platform === 'ANY PLATFORM'
                ? 'Has audience demographics (any platform)'
                : `Has audience demographics (${platform})`,
        );
    }
    if (filters.require_engagement_rate) {
        parts.push('Has engagement rate');
    }
    if (filters.language) parts.push(`Lang ${filters.language}`);
    if (filters.region) parts.push(`Region ${filters.region}`);
    return parts;
};

const growthLabels = (filters: AISearchResult['filters']): string[] => {
    const parts: string[] = [];
    const addFollower = (label: string, min?: number, months?: number) => {
        if (!min || min <= 0) return;
        parts.push(`${label} followers +${min}% / ${months && months > 0 ? months : 6}mo`);
    };
    const addER = (label: string, min?: number, months?: number) => {
        if (!min || min <= 0) return;
        parts.push(`${label} ER growth +${min}% / ${months && months > 0 ? months : 6}mo`);
    };
    addFollower('YT', filters.youtube_follower_growth_min_pct, filters.youtube_follower_growth_months);
    addFollower('IG', filters.instagram_follower_growth_min_pct, filters.instagram_follower_growth_months);
    addFollower('TT', filters.tiktok_follower_growth_min_pct, filters.tiktok_follower_growth_months);
    addER('IG', filters.instagram_engagement_growth_min_pct, filters.instagram_engagement_growth_months);
    addER('TT', filters.tiktok_engagement_growth_min_pct, filters.tiktok_engagement_growth_months);
    return parts;
};

const { Panel } = Collapse;

type SearchHelpSection = {
    key: string;
    title: string;
    description: string;
    examples: string[];
};

const SEARCH_HELP_SECTIONS: SearchHelpSection[] = [
    {
        key: 'platform',
        title: 'Platform & followers',
        description: 'Filter by network and follower count. Size words work too: nano, micro, mid, macro, mega.',
        examples: [
            'TikTok micro creators with 50k–200k followers',
            'Instagram creators with at least 100k followers',
        ],
    },
    {
        key: 'engagement',
        title: 'Engagement rate',
        description: 'Current engagement rate on Instagram or TikTok (e.g. 5% = at least 5% ER).',
        examples: [
            'IG creators with at least 5% engagement rate',
            'TikTok creators with engagement above 3%',
        ],
    },
    {
        key: 'growth',
        title: 'Follower & ER growth',
        description: 'Growth over time from fetch history. Default window is 6 months unless you specify another period.',
        examples: [
            'IG creators with at least 30% follower growth in the last 6 months',
            'Instagram creators with 20% engagement rate growth over 3 months',
        ],
    },
    {
        key: 'audience',
        title: 'Audience demographics',
        description: 'Inferred follower audience (gender, age, country, language). Requires a demographics scan on the creator.',
        examples: [
            'IG creators with at least 60% male audience',
            'TikTok creators with young audience (18-24) and Greek-speaking followers',
            'Instagram creators with female audience over 50% and demographics data',
        ],
    },
    {
        key: 'creator',
        title: 'Creator profile & niche',
        description: 'Creator tags (gender, content category, channel type), language, region, and name search.',
        examples: [
            'Female gaming creators on YouTube',
            'Greek Instagram beauty creators',
            'Creator named anna with fitness content',
        ],
    },
];

const AISearchBar: React.FC<Props> = ({ onResults, tagOptions }) => {
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeResult, setActiveResult] = useState<AISearchResult | null>(null);
    const [helpOpen, setHelpOpen] = useState(false);

    // Build a map from "categoryId,valueId" → { label, color } for tag badge rendering
    const tagInfoMap = React.useMemo(() => {
        const map = new Map<string, { label: string; color: string }>();
        if (!tagOptions) return map;
        for (const cat of tagOptions) {
            for (const child of (cat.children ?? [])) {
                map.set(child.value, { label: child.label, color: child.color ?? '#d9d9d9' });
            }
        }
        return map;
    }, [tagOptions]);

    const handleSearch = useCallback(async () => {
        const q = query.trim();
        if (!q) return;
        setLoading(true);
        setError(null);
        try {
            const resp = await axiosApiInstance.post<AISearchResult>('/api/creators/ai-search', { query: q });
            setActiveResult(resp.data);
            onResults(resp.data);
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? 'AI search failed';
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [query, onResults]);

    const handleClear = () => {
        setQuery('');
        setActiveResult(null);
        setError(null);
        onResults(null);
    };

    const isActive = activeResult !== null;

    return (
        <div
            style={{
                padding: '10px 16px',
                background: isActive ? '#f6f0ff' : '#fafafa',
                border: `1px solid ${isActive ? '#b37feb' : '#d9d9d9'}`,
                borderRadius: 8,
                marginBottom: 12,
                transition: 'all 0.2s',
            }}
        >
            {/* ── Input row ── */}
            <Space.Compact style={{ width: '100%' }}>
                <Input
                    prefix={
                        <Tooltip title="AI natural-language search">
                            <RobotOutlined style={{ color: isActive ? '#722ed1' : '#999' }} />
                        </Tooltip>
                    }
                    placeholder='e.g. "IG creators with female audience, 5%+ engagement, 100k+ followers"'
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onPressEnter={handleSearch}
                    disabled={loading}
                    style={{
                        borderColor: isActive ? '#b37feb' : undefined,
                        boxShadow: isActive ? '0 0 0 2px rgba(114,46,209,0.1)' : undefined,
                    }}
                    allowClear={!loading}
                />
                <Button
                    type="primary"
                    icon={loading ? <Spin size="small" /> : <SearchOutlined />}
                    onClick={handleSearch}
                    disabled={loading || !query.trim()}
                    style={{
                        backgroundColor: '#722ed1',
                        borderColor: '#722ed1',
                    }}
                >
                    {loading ? 'Thinking…' : 'AI Search'}
                </Button>
                {isActive && (
                    <Button
                        icon={<CloseCircleOutlined />}
                        onClick={handleClear}
                        title="Clear AI search – return to normal table"
                    >
                        Clear
                    </Button>
                )}
            </Space.Compact>

            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    type="link"
                    size="small"
                    icon={<InfoCircleOutlined />}
                    onClick={() => setHelpOpen((open) => !open)}
                    style={{ padding: 0, height: 'auto', color: '#722ed1', fontSize: 12 }}
                >
                    {helpOpen ? 'Hide search tips' : 'What can I search for?'}
                </Button>
            </div>

            {helpOpen && (
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                        Describe what you want in plain language. The AI turns it into filters — combine multiple
                        criteria in one sentence. Click an example to try it.
                    </Text>
                    <Collapse bordered={false} style={{ background: 'transparent' }}>
                        {SEARCH_HELP_SECTIONS.map((section) => (
                            <Panel
                                key={section.key}
                                header={<span style={{ fontSize: 13, fontWeight: 500 }}>{section.title}</span>}
                            >
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                                    {section.description}
                                </Text>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {section.examples.map((example) => (
                                        <Tag
                                            key={example}
                                            style={{ cursor: 'pointer', borderRadius: 12, margin: 0 }}
                                            onClick={() => setQuery(example)}
                                        >
                                            {example}
                                        </Tag>
                                    ))}
                                </div>
                            </Panel>
                        ))}
                    </Collapse>
                </div>
            )}

            {/* ── Error ── */}
            {error && (
                <Alert
                    message={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    style={{ marginTop: 8 }}
                />
            )}

            {/* ── Result summary ── */}
            {isActive && activeResult && (
                <div style={{ marginTop: 10 }}>
                    {/* Reasoning */}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <RobotOutlined style={{ color: '#722ed1', marginRight: 4 }} />
                        <em>{activeResult.reasoning}</em>
                    </Text>

                    {/* Applied filters as tags */}
                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>

                        {/* Platform badge */}
                        {activeResult.filters.platform && (
                            <Tag color={PLATFORM_COLOR[activeResult.filters.platform] ?? 'blue'} style={{ borderRadius: 12, fontWeight: 500 }}>
                                {activeResult.filters.platform}
                            </Tag>
                        )}

                        {/* Follower ranges */}
                        {platformFollowerLabel(activeResult.filters).map((lbl) => (
                            <Tag key={lbl} color="geekblue" style={{ borderRadius: 12 }}>
                                {lbl} followers
                            </Tag>
                        ))}

                        {/* Engagement rate */}
                        {engagementRateLabels(activeResult.filters).map((lbl) => (
                            <Tag key={lbl} color="gold" style={{ borderRadius: 12 }}>
                                {lbl}
                            </Tag>
                        ))}

                        {/* Audience demographics & account signals */}
                        {audienceLabels(activeResult.filters).map((lbl) => (
                            <Tag key={lbl} color="purple" style={{ borderRadius: 12 }}>
                                {lbl}
                            </Tag>
                        ))}

                        {/* Growth filters */}
                        {growthLabels(activeResult.filters).map((lbl) => (
                            <Tag key={lbl} color="green" style={{ borderRadius: 12 }}>
                                {lbl}
                            </Tag>
                        ))}

                        {/* Tag filters resolved to names + colours */}
                        {activeResult.filters.tag_filters.map((filterKey) => {
                            const info = tagInfoMap.get(filterKey);
                            const bg = info?.color ?? '#d9d9d9';
                            const palette = generate(bg);
                            const textColor = getPaletteColor(bg, palette);
                            return (
                                <Tag
                                    key={filterKey}
                                    color={bg}
                                    style={{ color: textColor, borderRadius: 12 }}
                                >
                                    {info?.label ?? filterKey}
                                </Tag>
                            );
                        })}

                        {/* Result count */}
                        <Tag color="purple" style={{ borderRadius: 12, marginLeft: 'auto' }}>
                            {activeResult.total} creator{activeResult.total !== 1 ? 's' : ''}
                        </Tag>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AISearchBar;
